import { useDeferredValue, useEffect, useState, type FormEvent, type KeyboardEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, BookPlus, Check, LibraryBig, LoaderCircle, Quote, RotateCcw, Search, Sparkles, X } from "lucide-react";
import type { WordSense } from "@preptrack/contracts";

import { Button, EmptyState, Field, Input, PageHeader, Textarea } from "@/components/primitives";
import { ReadingEntryTabs } from "@/components/reading-entry-tabs";
import { ResetPageDialog } from "@/components/reset-page-dialog";
import { useAuth } from "@/components/auth-context";
import { apiFetch } from "@/lib/api";
import { HighlightedHeadword } from "@/components/highlighted-headword";
import { WordPronunciation } from "@/components/word-pronunciation";
import { sensesToText, WordSenses } from "@/components/word-senses";
import { completedSenseDrafts, emptySenseDraft, senseDraftsFromSenses, SenseRowsEditor, type SenseDraft } from "@/components/sense-rows-editor";

interface PersonalWord {
  headword: string;
  memberships: Array<{ code: string; label: string; kind: string }>;
}
interface Suggestion {
  id: string;
  headword: string;
  phoneticUk: string | null;
  phoneticUs: string | null;
  senses: WordSense[];
}
interface ExamSentence {
  id: string;
  content: string;
  year: number;
  examCode: string;
  examType: string;
  section: string;
  label: string | null;
}

interface ManualEntryDraft {
  headword: string;
  senses: SenseDraft[];
  notes: string;
  sentence: string;
  selectedWordId: string | undefined;
  selectedSentence: ExamSentence | null;
  ai: boolean;
}

const manualEntryStorageKey = (userId: string) => `preptrack:manual-entry:${userId}`;

const emptyManualEntryDraft = (): ManualEntryDraft => ({
  headword: "",
  senses: [emptySenseDraft()],
  notes: "",
  sentence: "",
  selectedWordId: undefined,
  selectedSentence: null,
  ai: true,
});

const readManualEntryDraft = (storageKey: string | null): ManualEntryDraft => {
  if (!storageKey) return emptyManualEntryDraft();
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(storageKey) ?? "null");
    if (!value || typeof value !== "object") return emptyManualEntryDraft();
    const record = value as Record<string, unknown>;
    if (record.version !== 2) return emptyManualEntryDraft();
    const rawSentence = record.selectedSentence;
    const sentenceRecord = rawSentence && typeof rawSentence === "object"
      ? rawSentence as Record<string, unknown>
      : null;
    const selectedSentence = sentenceRecord
      && typeof sentenceRecord.id === "string"
      && typeof sentenceRecord.content === "string"
      && typeof sentenceRecord.year === "number"
      && typeof sentenceRecord.section === "string"
      ? {
          id: sentenceRecord.id,
          content: sentenceRecord.content,
          year: sentenceRecord.year,
          examCode: typeof sentenceRecord.examCode === "string" ? sentenceRecord.examCode : "201",
          examType: typeof sentenceRecord.examType === "string" ? sentenceRecord.examType : sentenceRecord.year < 2010 ? "统一卷" : "英语一",
          section: sentenceRecord.section,
          label: typeof sentenceRecord.label === "string" ? sentenceRecord.label : null,
        }
      : null;
    return {
      headword: typeof record.headword === "string" ? record.headword : "",
      senses: Array.isArray(record.senses)
        ? senseDraftsFromSenses(record.senses.flatMap((item) => {
            if (!item || typeof item !== "object") return [];
            const sense = item as Record<string, unknown>;
            return typeof sense.definitionZh === "string"
              ? [{ partOfSpeech: typeof sense.partOfSpeech === "string" ? sense.partOfSpeech : null, definitionZh: sense.definitionZh }]
              : [];
          }))
        : [emptySenseDraft()],
      notes: typeof record.notes === "string" ? record.notes : "",
      sentence: typeof record.sentence === "string" ? record.sentence : "",
      selectedWordId: typeof record.selectedWordId === "string" ? record.selectedWordId : undefined,
      selectedSentence,
      ai: typeof record.ai === "boolean" ? record.ai : true,
    };
  } catch {
    window.localStorage.removeItem(storageKey);
    return emptyManualEntryDraft();
  }
};

export function MyWordsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const storageKey = user ? manualEntryStorageKey(user.userId) : null;
  const [initialDraft] = useState(() => readManualEntryDraft(storageKey));
  const query = useQuery({
    queryKey: ["user-words"],
    queryFn: () => apiFetch<{ words: PersonalWord[] }>("/user-words"),
  });
  const words = query.data?.words ?? [];

  const [headword, setHeadword] = useState(initialDraft.headword);
  const [senses, setSenses] = useState<SenseDraft[]>(initialDraft.senses);
  const [notes, setNotes] = useState(initialDraft.notes);
  const [sentence, setSentence] = useState(initialDraft.sentence);
  const [selectedWordId, setSelectedWordId] = useState<string | undefined>(initialDraft.selectedWordId);
  const [selectedSentence, setSelectedSentence] = useState<ExamSentence | null>(initialDraft.selectedSentence);
  const [activeWordIndex, setActiveWordIndex] = useState(0);
  const [headwordOpen, setHeadwordOpen] = useState(false);
  const [sentenceOpen, setSentenceOpen] = useState(false);

  const [ai, setAi] = useState(initialDraft.ai);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const hasDraft = Boolean(headword || completedSenseDrafts(senses).length || notes || sentence || selectedWordId || selectedSentence) || !ai;

  const deferredHeadword = useDeferredValue(headword.trim().toLowerCase());
  const suggestionQuery = useQuery({
    queryKey: ["suggest", "manual-entry", deferredHeadword],
    queryFn: () => apiFetch<{ suggestions: Suggestion[] }>(`/words/suggest?q=${encodeURIComponent(deferredHeadword)}`),
    enabled: deferredHeadword.length > 0,
    staleTime: 30_000,
  });
  const suggestions = suggestionQuery.data?.suggestions ?? [];
  const exactWord = suggestions.find((item) => item.headword.toLowerCase() === deferredHeadword);
  const bestWord = suggestions.find((item) => item.id === selectedWordId) ?? exactWord ?? suggestions[0];
  const bestWordMeaning = bestWord ? sensesToText(bestWord.senses) : "";
  const completedSenses = completedSenseDrafts(senses);
  const previewSenses: WordSense[] = completedSenses.length
    ? completedSenses.map((sense, sortOrder) => ({ id: `preview-${sortOrder}`, ...sense, sortOrder }))
    : bestWord?.senses ?? [];
  const existingPersonal = words.find((item) => item.headword.toLowerCase() === deferredHeadword);
  const duplicateMemberships = existingPersonal?.memberships ?? [];

  useEffect(() => {
    setActiveWordIndex(0);
  }, [deferredHeadword, suggestions.length]);

  useEffect(() => {
    if (!storageKey) return;
    if (!hasDraft) {
      window.localStorage.removeItem(storageKey);
      return;
    }
    window.localStorage.setItem(storageKey, JSON.stringify({
      version: 2,
      headword,
      senses,
      notes,
      sentence,
      selectedWordId,
      selectedSentence,
      ai,
    }));
  }, [ai, hasDraft, headword, notes, selectedSentence, selectedWordId, senses, sentence, storageKey]);

  const sentenceTerm = useDeferredValue(sentence.trim());
  const sentenceLookupTerm = sentenceTerm.length > 0
    ? sentenceTerm
    : bestWord?.headword ?? deferredHeadword;
  const sentenceQuery = useQuery({
    queryKey: ["exam-sentences", "manual-entry", sentenceLookupTerm],
    queryFn: () => apiFetch<{ sentences: ExamSentence[] }>(`/exam-sentences/search?q=${encodeURIComponent(sentenceLookupTerm)}&headword=${encodeURIComponent(bestWord?.headword ?? deferredHeadword)}`),
    enabled: sentenceLookupTerm.length >= 2 && !selectedSentence,
    staleTime: 30_000,
  });
  const sentenceSuggestions = sentenceQuery.data?.sentences ?? [];

  const wordPanelOpen = headwordOpen && deferredHeadword.length > 0;
  const sentencePanelOpen = sentenceOpen
    && !selectedSentence
    && sentenceLookupTerm.length >= 2;

  const applyWordSuggestion = (item: Suggestion) => {
    setHeadword(item.headword);
    setSelectedWordId(item.id);
    setHeadwordOpen(false);
    setSentenceOpen(false);
    setSelectedSentence(null);
  };

  const handleHeadwordKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setHeadwordOpen(false);
      return;
    }
    if (!suggestions.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!headwordOpen) {
        setHeadwordOpen(true);
        setActiveWordIndex(0);
      } else {
        setActiveWordIndex((index) => (index + 1) % suggestions.length);
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHeadwordOpen(true);
      setActiveWordIndex((index) => (index - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter" && headwordOpen) {
      const selected = suggestions[activeWordIndex] ?? suggestions[0];
      if (selected) {
        event.preventDefault();
        applyWordSuggestion(selected);
      }
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await apiFetch("/user-words", {
        method: "POST",
        body: JSON.stringify({
          headword,
          senses: completedSenseDrafts(senses),
          notes: notes || undefined,
          sourceSentence: sentence || undefined,
          examSentenceId: selectedSentence?.id,
          useAiCompletion: ai,
        }),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["user-words"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["word-books"] }),
      ]);
      setSaved(headword);
      if (storageKey) window.localStorage.removeItem(storageKey);
      setHeadword("");
      setSenses([emptySenseDraft()]);
      setNotes("");
      setSentence("");
      setSelectedWordId(undefined);
      setSelectedSentence(null);
      setHeadwordOpen(false);
      setSentenceOpen(false);
    } catch {
      setError("保存失败，请稍后重试；当前填写内容已保留。");
    } finally {
      setSaving(false);
    }
  };

  const resetPage = () => {
    if (storageKey) window.localStorage.removeItem(storageKey);
    setHeadword("");
    setSenses([emptySenseDraft()]);
    setNotes("");
    setSentence("");
    setSelectedWordId(undefined);
    setSelectedSentence(null);
    setActiveWordIndex(0);
    setHeadwordOpen(false);
    setSentenceOpen(false);
    setAi(true);
    setSaved("");
    setError("");
    setResetOpen(false);
  };

  return (
    <div className="page my-words-page">
      <PageHeader
        title="阅读生词"
        description="记录阅读时遇到的词，选择手动录入或拍照识别，确认后再保存到个人词库。"
        action={<div className="page-heading-actions"><Link to="/app/$subject/word-library" params={{ subject: "english" }} className="button button--secondary button--md"><LibraryBig /> 管理词库</Link><Button type="button" variant="ghost" onClick={() => setResetOpen(true)} disabled={!hasDraft || saving}><RotateCcw /> 重置本页</Button></div>}
      />
      <ReadingEntryTabs active="manual" />

      <div className="manual-entry-layout">
        <form className="entry-form" onSubmit={submit}>
          <div className="section-heading-row"><h2><BookPlus /> 手动录入</h2><span>输入时即时匹配</span></div>

          <div
            className="entry-field-shell"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setHeadwordOpen(false);
            }}
          >
            <Field label="英文单词" htmlFor="headword">
              <Input
                id="headword"
                required
                pattern="[A-Za-z][A-Za-z '-]*"
                value={headword}
                onChange={(event) => {
                  setHeadword(event.target.value);
                  setSelectedWordId(undefined);
                  setSelectedSentence(null);
                  setHeadwordOpen(true);
                  setSentenceOpen(false);
                }}
                onFocus={() => {
                  if (deferredHeadword) setHeadwordOpen(true);
                  setSentenceOpen(false);
                }}
                onKeyDown={handleHeadwordKeyDown}
                placeholder="例如 resilient"
                role="combobox"
                aria-autocomplete="list"
                aria-controls="entry-word-suggestions"
                aria-expanded={wordPanelOpen}
                aria-activedescendant={wordPanelOpen && suggestions[activeWordIndex] ? `entry-word-${suggestions[activeWordIndex].id}` : undefined}
              />
            </Field>
            <div
              id="entry-word-suggestions"
              className={wordPanelOpen ? "entry-assist-panel entry-assist-panel--word entry-assist-panel--open" : "entry-assist-panel entry-assist-panel--word"}
              role="listbox"
              aria-label="词条建议"
              aria-hidden={!wordPanelOpen}
            >
              {suggestionQuery.isFetching && suggestions.length === 0 ? (
                <div className="entry-assist-state"><LoaderCircle /> 正在匹配词典</div>
              ) : null}
              {suggestions.map((item, index) => (
                <div
                  key={item.id}
                  className={index === activeWordIndex ? "entry-word-option entry-word-option--active" : "entry-word-option"}
                  style={{ animationDelay: `${index * 28}ms` }}
                  onMouseEnter={() => setActiveWordIndex(index)}
                >
                  <button
                    id={`entry-word-${item.id}`}
                    type="button"
                    className="entry-word-option__select"
                    role="option"
                    aria-selected={index === activeWordIndex}
                    onFocus={() => setActiveWordIndex(index)}
                    onClick={() => applyWordSuggestion(item)}
                  >
                    <span className="entry-word-option__head"><strong>{item.headword}</strong></span>
                    <WordSenses senses={item.senses} variant="compact" />
                    <ArrowRight />
                  </button>
                  <WordPronunciation wordId={item.id} headword={item.headword} phoneticUk={item.phoneticUk} phoneticUs={item.phoneticUs} compact />
                </div>
              ))}
              {!suggestionQuery.isFetching && deferredHeadword && suggestions.length === 0 ? (
                <div className="entry-assist-state entry-assist-state--empty">词典中暂无匹配，仍可作为个人生词保存。</div>
              ) : null}
            </div>
          </div>

          <Field label="词义" htmlFor="manual-sense-definition-0" optional hint="每行对应一个词性；同一词性的多个释义用分号分隔。">
            <SenseRowsEditor value={senses} onChange={setSenses} idPrefix="manual-sense" />
          </Field>
          {bestWord && bestWordMeaning ? (
            <div className="entry-best-match-shell">
              <button className="entry-best-match entry-best-match--inline" type="button" onClick={() => setSenses(senseDraftsFromSenses(bestWord.senses))}>
                <span className="entry-best-match__label"><Sparkles /> 采用词典释义 <small>{bestWord.headword}</small></span>
                <span className="entry-best-match__content"><WordSenses senses={bestWord.senses} variant="compact" /></span>
              </button>
              <WordPronunciation wordId={bestWord.id} headword={bestWord.headword} phoneticUk={bestWord.phoneticUk} phoneticUs={bestWord.phoneticUs} compact />
            </div>
          ) : null}

          <Field label="笔记" htmlFor="notes" optional>
            <Textarea id="notes" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="联想、易混词、固定搭配…" rows={3} />
          </Field>

          <div
            className="entry-field-shell"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setSentenceOpen(false);
            }}
          >
            <Field label="来源句子" htmlFor="sentence" optional hint="可直接粘贴片段；留空时会按当前单词匹配 2000–2026 英语一、英语二真题。">
              <div className="input-with-icon">
                <Textarea
                  id="sentence"
                  value={sentence}
                  onChange={(event) => {
                    setSentence(event.target.value);
                    setSelectedSentence(null);
                    setSentenceOpen(true);
                  }}
                  onFocus={() => {
                    setSentenceOpen(true);
                    setHeadwordOpen(false);
                  }}
                  placeholder="搜索、粘贴，或从匹配结果中采用原句"
                  rows={3}
                  role="combobox"
                  aria-autocomplete="list"
                  aria-controls="entry-sentence-suggestions"
                  aria-expanded={sentencePanelOpen}
                />
                <Search />
              </div>
            </Field>
            <div
              id="entry-sentence-suggestions"
              className={sentencePanelOpen ? "entry-assist-panel entry-assist-panel--sentence entry-assist-panel--open" : "entry-assist-panel entry-assist-panel--sentence"}
              role="listbox"
              aria-label="真题原句建议"
              aria-hidden={!sentencePanelOpen}
            >
              <div className="entry-sentence-panel__heading"><Quote /> <span>{sentenceTerm ? "按输入片段匹配" : `包含 “${bestWord?.headword ?? headword}” 的真题原句`}</span></div>
              {sentenceQuery.isFetching && sentenceSuggestions.length === 0 ? (
                <div className="entry-assist-state"><LoaderCircle /> 正在检索真题语料</div>
              ) : null}
              {sentenceSuggestions.map((item, index) => (
                <button
                  type="button"
                  role="option"
                  key={item.id}
                  style={{ animationDelay: `${index * 32}ms` }}
                  onClick={() => {
                    setSentence(item.content);
                    setSelectedSentence(item);
                    setSentenceOpen(false);
                  }}
                >
                  <span><HighlightedHeadword text={item.content} headword={headword} /></span>
                  <small>{item.year} {item.examType} · {item.section} {item.label}</small>
                </button>
              ))}
              {!sentenceQuery.isFetching && sentenceSuggestions.length === 0 ? (
                <div className="entry-assist-state entry-assist-state--empty">未找到真题原句，可保留手动输入的来源。</div>
              ) : null}
            </div>
            {selectedSentence ? (
              <div className="entry-selected-match">
                <span><Check /> 已关联 {selectedSentence.year} {selectedSentence.examType}真题 · {selectedSentence.section}</span>
                <button type="button" onClick={() => { setSelectedSentence(null); setSentenceOpen(true); }} aria-label="取消真题原句关联"><X /></button>
              </div>
            ) : null}
          </div>

          <label className="check-row">
            <input type="checkbox" checked={ai} onChange={(event) => setAi(event.target.checked)} />
            <span><strong><Sparkles /> 自动补全空缺词义</strong><small>仅当词典和手动输入都没有释义时调用 MiMo；真题来源不会由 AI 生成。</small></span>
          </label>
          <Button variant="primary" size="lg" type="submit" disabled={!headword.trim() || saving}>{saving ? "正在保存…" : "确认保存到生词本"} <ArrowRight /></Button>
          {saved ? <p className="save-message" role="status">“{saved}” 已加入阅读生词。</p> : null}
          {error ? <p className="error-strip" role="alert">{error}</p> : null}
        </form>

        <aside className="word-preview">
          <div className="section-heading-row"><h2>预览</h2><span>将加入生词本</span></div>
          {headword ? (
            <>
              <div className="preview-word">
                <h3>{headword}</h3>
                {bestWord ? <WordPronunciation wordId={bestWord.id} headword={bestWord.headword} phoneticUk={bestWord.phoneticUk} phoneticUs={bestWord.phoneticUs} /> : null}
              </div>
              <dl>
                <div><dt>词义</dt><dd><WordSenses senses={previewSenses} empty={ai ? "保存时按需由 MiMo 补全" : "未填写"} /></dd></div>
                <div><dt>笔记</dt><dd>{notes || "—"}</dd></div>
                <div><dt>来源句子</dt><dd>{sentence ? <HighlightedHeadword text={sentence} headword={headword} /> : "—"}{selectedSentence ? <small className="preview-source-meta"><Check /> 已关联真题原句</small> : null}</dd></div>
              </dl>
              <div className="duplicate-block">
                <p className="duplicate-block__heading">词库归属</p>
                {exactWord ? (
                  duplicateMemberships.length ? (
                    <>
                      <div className="preview-memberships">
                        {duplicateMemberships.map((membership) => (
                          <span
                            className={membership.kind === "official" ? "preview-membership preview-membership--official" : "preview-membership"}
                            key={membership.code}
                          >
                            <Check />
                            {membership.label}
                          </span>
                        ))}
                      </div>
                      <small>不会重复创建词典词条，你的笔记会单独保留。</small>
                    </>
                  ) : (
                    <>
                      <span className="preview-dictionary-match"><Check /> 已匹配标准词典</span>
                      <small>保存后会链接到现有标准词条。</small>
                    </>
                  )
                ) : <span className="preview-dictionary-empty">当前未发现同名标准词条</span>}
              </div>
            </>
          ) : (
            <EmptyState title="等待一个单词" description="输入单词后，这里会显示词义、来源和词库归属。" />
          )}
        </aside>
      </div>

      <ResetPageDialog
        open={resetOpen}
        title="重置手动录入？"
        description="当前单词、词义、笔记、来源句子和未保存的关联信息将被清除。已经保存到词库的生词不会受影响。"
        onClose={() => setResetOpen(false)}
        onConfirm={resetPage}
      />
    </div>
  );
}
